import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

interface PRState {
    myPRs: any[];
    pendingApprovals: any[];
    currentPR: any | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: PRState = {
    myPRs: [],
    pendingApprovals: [],
    currentPR: null,
    isLoading: false,
    error: null,
};

export const fetchMyPRs = createAsyncThunk('pr/fetchMyPRs', async (_, { rejectWithValue }) => {
    try {
        const response = await apiService.getMyPRs();
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message);
    }
});

export const fetchPendingApprovals = createAsyncThunk(
    'pr/fetchPendingApprovals',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getPendingApprovals();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchPRById = createAsyncThunk(
    'pr/fetchById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await apiService.getPR(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const createPR = createAsyncThunk(
    'pr/create',
    async (prData: any, { rejectWithValue }) => {
        try {
            const response = await apiService.createPR(prData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

const prSlice = createSlice({
    name: 'pr',
    initialState,
    reducers: {
        clearCurrentPR: (state) => {
            state.currentPR = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch My PRs
        builder.addCase(fetchMyPRs.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchMyPRs.fulfilled, (state, action) => {
            state.isLoading = false;
            state.myPRs = action.payload;
        });
        builder.addCase(fetchMyPRs.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch Pending Approvals
        builder.addCase(fetchPendingApprovals.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPendingApprovals.fulfilled, (state, action) => {
            state.isLoading = false;
            state.pendingApprovals = action.payload;
        });
        builder.addCase(fetchPendingApprovals.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch PR by ID
        builder.addCase(fetchPRById.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPRById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPR = action.payload;
        });
        builder.addCase(fetchPRById.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create PR
        builder.addCase(createPR.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createPR.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPR = action.payload;
        });
        builder.addCase(createPR.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    },
});

export const { clearCurrentPR, clearError } = prSlice.actions;
export default prSlice.reducer;
